/**
 * Apollo GraphQL Client Configuration for AI Services
 * Implements offline-first caching, error handling, and retry logic
 */

import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
  Observable,
  NextLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { logger } from '../utils/logger';

// Types for AI-specific caching and configuration
export interface AIClientConfig {
  uri: string;
  enableOfflineCache: boolean;
  enableRetry: boolean;
  maxRetryAttempts: number;
  retryDelay: number;
  enablePersistedQueries: boolean;
  authToken?: string;
  onError?: (error: any) => void;
}

export interface CacheConfig {
  typePolicies: {
    [key: string]: {
      keyFields?: string[] | false;
      fields?: {
        [fieldName: string]: {
          merge?: boolean | ((existing: any, incoming: any) => any);
          read?: (existing: any, options: any) => any;
        };
      };
    };
  };
}

// AI-specific cache configuration with offline-first policies
const aiCacheConfig: CacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        // Journey queries - cache for 5 minutes
        myJourneys: {
          merge: (
            existing = { journeys: [], total: 0, hasMore: false },
            incoming
          ) => {
            return {
              ...incoming,
              journeys: [...(existing.journeys || []), ...incoming.journeys],
            };
          },
        },
        // Recommendations - cache for 1 minute, prefer fresh data
        recommendations: {
          merge: false, // Always replace with fresh data
        },
        contentRecommendations: {
          merge: false, // Always replace with fresh data
        },
        // Mastery data - cache for 10 minutes
        mastery: {
          merge: (_existing, incoming) => incoming,
        },
        // Analytics - cache for 30 minutes
        advancedAnalytics: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    Journey: {
      keyFields: ['id'],
      fields: {
        weeks: {
          merge: (_existing = [], incoming) => incoming,
        },
        progress: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    JourneyWeek: {
      keyFields: ['id'],
      fields: {
        activities: {
          merge: (_existing = [], incoming) => incoming,
        },
      },
    },
    JourneyActivity: {
      keyFields: ['id'],
      fields: {
        progress: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    ContentRecommendation: {
      keyFields: ['id'],
      fields: {
        // Recommendations should always use fresh data
        recommendationScore: {
          merge: (_existing, incoming) => incoming,
        },
        recommendationReason: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    MasteryState: {
      keyFields: ['userId', 'skillId'],
      fields: {
        masteryProbability: {
          merge: (_existing, incoming) => incoming,
        },
        abilityEstimate: {
          merge: (_existing, incoming) => incoming,
        },
      },
    },
    User: {
      keyFields: ['id'],
    },
  },
};

// Create AI-optimized cache with conflict resolution
function createAICache(): InMemoryCache {
  return new InMemoryCache({
    ...aiCacheConfig,
    resultCaching: true,
  });
}

// Authentication link
function createAuthLink(getToken?: () => string | null): ApolloLink {
  return setContext((_, { headers }) => {
    const token = getToken?.();
    return {
      headers: {
        ...headers,
        ...(token && { authorization: `Bearer ${token}` }),
        'Content-Type': 'application/json',
      },
    };
  });
}

// Error handling link with AI-specific error recovery
function createErrorLink(onErrorCallback?: (error: any) => void): ApolloLink {
  return onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        const errorInfo = {
          message,
          locations,
          path,
          extensions,
          operation: operation.operationName,
        };

        logger.error('[GraphQL Error]:', errorInfo);
        onErrorCallback?.(errorInfo);

        // Handle specific AI service errors
        if (extensions?.['code'] === 'AI_SERVICE_UNAVAILABLE') {
          // Could implement fallback to cached recommendations
          logger.warn('AI service unavailable, using cached data');
        }
      });
    }

    if (networkError) {
      const errorInfo = {
        message: networkError.message,
        operation: operation.operationName,
        networkError: true,
      };

      logger.error('[Network Error]:', errorInfo);
      onErrorCallback?.(errorInfo);

      // Handle network errors for offline scenarios
      if (networkError.message.includes('fetch')) {
        logger.warn('Network unavailable, using cached AI data');
      }
    }
  });
}

// Retry link with exponential backoff for AI services
function createRetryLink(
  maxRetryAttempts: number = 3,
  retryDelay: number = 1000
): RetryLink {
  return new RetryLink({
    delay: {
      initial: retryDelay,
      max: retryDelay * 8,
      jitter: true,
    },
    attempts: {
      max: maxRetryAttempts,
      retryIf: (error, _operation) => {
        // Retry on network errors and specific GraphQL errors
        return (
          !!error &&
          (error.networkError ||
            (error.graphQLErrors &&
              error.graphQLErrors.some(
                (e: any) =>
                  e.extensions?.code === 'INTERNAL_SERVER_ERROR' ||
                  e.extensions?.code === 'AI_SERVICE_TIMEOUT'
              )))
        );
      },
    },
  });
}

// Offline queue link for handling requests when offline
function createOfflineQueueLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let subscription: any;

      // Check if online
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        // Queue the operation for when we're back online
        const queuedOperation = {
          operation,
          observer,
          timestamp: Date.now(),
        };

        // Store in localStorage for persistence
        const queue = JSON.parse(
          localStorage.getItem('apollo-offline-queue') || '[]'
        );
        queue.push(queuedOperation);
        localStorage.setItem('apollo-offline-queue', JSON.stringify(queue));

        // Listen for online event
        const handleOnline = () => {
          window.removeEventListener('online', handleOnline);
          // Process queued operations
          processOfflineQueue(forward);
        };
        window.addEventListener('online', handleOnline);

        return () => {
          window.removeEventListener('online', handleOnline);
        };
      }

      // Normal online operation
      subscription = forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });

      return () => {
        if (subscription) subscription.unsubscribe();
      };
    });
  });
}

// Process queued offline operations
function processOfflineQueue(forward: NextLink) {
  const queue = JSON.parse(
    localStorage.getItem('apollo-offline-queue') || '[]'
  );

  queue.forEach(({ operation, observer }: any) => {
    forward(operation).subscribe({
      next: observer.next.bind(observer),
      error: observer.error.bind(observer),
      complete: observer.complete.bind(observer),
    });
  });

  // Clear the queue
  localStorage.removeItem('apollo-offline-queue');
}

// Main Apollo Client factory
export function createAIApolloClient(
  config: AIClientConfig
): ApolloClient<any> {
  const {
    uri,
    enableOfflineCache = true,
    enableRetry = true,
    maxRetryAttempts = 3,
    retryDelay = 1000,
    authToken,
    onError: onErrorCallback,
  } = config;

  // Create HTTP link
  const httpLink = createHttpLink({
    uri,
    credentials: 'include',
  });

  // Build link chain
  const links: ApolloLink[] = [];

  // Add persisted queries for performance (disabled for now)
  // if (enablePersistedQueries) {
  //   links.push(
  //     createPersistedQueryLink({
  //       sha256,
  //       useGETForHashedQueries: true,
  //     })
  //   );
  // }

  // Add error handling
  links.push(createErrorLink(onErrorCallback));

  // Add retry logic
  if (enableRetry) {
    links.push(createRetryLink(maxRetryAttempts, retryDelay));
  }

  // Add authentication
  links.push(createAuthLink(() => authToken || null));

  // Add offline queue if enabled
  if (enableOfflineCache) {
    links.push(createOfflineQueueLink());
  }

  // Add HTTP link last
  links.push(httpLink);

  // Create cache
  const cache = createAICache();

  // Create client
  const client = new ApolloClient({
    link: from(links),
    cache,
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: process.env['NODE_ENV'] === 'development',
    },
  });

  // Set up cache persistence for offline support
  if (enableOfflineCache && typeof window !== 'undefined') {
    // Restore cache from localStorage on startup
    const persistedCache = localStorage.getItem('apollo-cache-persist');
    if (persistedCache) {
      try {
        cache.restore(JSON.parse(persistedCache));
      } catch (error) {
        logger.warn('Failed to restore Apollo cache:', error);
        localStorage.removeItem('apollo-cache-persist');
      }
    }

    client.onResetStore(() => {
      localStorage.removeItem('apollo-cache-persist');
      return Promise.resolve();
    });

    // Debounced cache persistence
    let persistTimer: any;
    const persistCache = () => {
      clearTimeout(persistTimer);
      persistTimer = setTimeout(() => {
        try {
          const cacheData = cache.extract();
          localStorage.setItem(
            'apollo-cache-persist',
            JSON.stringify(cacheData)
          );
        } catch (error) {
          logger.warn('Failed to persist Apollo cache:', error);
        }
      }, 1000);
    };

    // Listen for cache changes
    client.onClearStore(() => {
      persistCache();
      return Promise.resolve();
    });
  }

  return client;
}

// Default client configuration
export const defaultAIClientConfig: AIClientConfig = {
  uri:
    process.env['NEXT_PUBLIC_GRAPHQL_ENDPOINT'] ||
    'http://localhost:8000/graphql',
  enableOfflineCache: true,
  enableRetry: true,
  maxRetryAttempts: 3,
  retryDelay: 1000,
  enablePersistedQueries: true,
};

// Export configured client instance
export const aiApolloClient = createAIApolloClient(defaultAIClientConfig);
